import { inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { CatchErrorService } from '@core/catch-error.service';
import { collection, doc, Firestore, onSnapshot, query, where } from "@angular/fire/firestore";
import { BehaviorSubject, combineLatest, defer, Observable, of } from 'rxjs';
import {catchError, map, shareReplay, startWith, switchMap, tap} from "rxjs/operators";
import { CommonService } from 'src/app/services/common/common.service';
import { UserManagementService } from 'src/app/services/user-management.service';
import { environment } from 'src/environments/environment';
import { CollectionReference, DocumentReference, getDocFromCache, Query } from "firebase/firestore";

@Injectable({
  providedIn: 'root'
})
export class GameStreamsService {

    constructor(
        private userManagementService: UserManagementService,
        private authService: AuthService,
        private catchService: CatchErrorService,
        private firestore: Firestore,
    ) {

    }

    private commonService = inject(CommonService);
    prizeUnlockID: any;
    yoursToPlay$ = new BehaviorSubject([]);
    prototypeGames$ = new BehaviorSubject<Data>([]);

    instantWinGames$ = defer(() => this.userManagementService?.userRequest$.pipe(
        switchMap((user) =>
          user
            ? this.authService
              .getAngularFireStore(`/general_modules/${environment.mirrorPath}game`, "snapshotChanges")
              .pipe(map((res: Data) => res?.payload?.data()))
              .pipe(
                switchMap((data) => {
                  const observablesInstantWin: Observable<any>[] = [];
                  if (data && data["instant_win"]) {
                    if (!data["instant_win"].ids) return of({ games: [] });
                    data["instant_win"].ids
                      .sort((a: any, b: any) => b - a)
                      .map((id) => {
                        observablesInstantWin.push(
                          this.authService
                            .getAngularFireStore(`general_modules/game/${environment.mirrorPath}instant_win/${id}`, "snapshotChanges")
                            .pipe(map((res: Data) => res?.payload?.data()))
                        );
                      });
                    // ✅ Check if array is empty before calling combineLatest
                    if (observablesInstantWin.length === 0) {
                      return of({ games: [] });
                    }
                    return combineLatest(observablesInstantWin).pipe(
                      map((instantGames) => {
                        return {
                          ...{ games: instantGames },
                          title: data["instant_win"].title,
                          sub_title: data["instant_win"].sub_title,
                        };
                      })
                    );
                  } else {
                    return of([]);
                  }
                })
              )
            : of([])
        )
    ));

    skillGames$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) =>
      user
        ? this.authService
          .getAngularFireStore(`general_modules/${environment.mirrorPath}game`, "snapshotChanges")
          .pipe(map((res: Data) => res?.payload?.data()))
          .pipe(
            switchMap((data) => {
              const observablesSkill: Observable<any>[] = [];
              if (data && data["skill"]) {
                if (!data["skill"].ids) return of({ games: [] });
                data["skill"].ids
                  .sort((a: any, b: any) => b - a)
                  .map((id) => {
                    observablesSkill.push(
                      this.authService
                        .getAngularFireStore(`/general_modules/game/${environment.mirrorPath}skill/${id}`, "snapshotChanges")
                        .pipe(map((res: Data) => res?.payload?.data()))
                    );
                  });
                // ✅ Check if array is empty before calling combineLatest
                if (observablesSkill.length === 0) {
                  return of({ games: [] });
                }
                return combineLatest(observablesSkill).pipe(
                  map((skillGames) => {
                    return {
                      ...{ games: skillGames },
                      title: data["skill"].title,
                      sub_title: data["skill"].sub_title,
                    };
                  })
                );
              } else {
                return of([]);
              }
            })
          )
        : of([])
    )
    );

    adventure$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) =>
      user
        ? this.authService
          .getAngularFireStore(`/general_modules/${environment.mirrorPath}game`, "snapshotChanges")
          .pipe(map((res: Data) => res?.payload?.data()))
          .pipe(
            switchMap((data) => {
              if (!data || !data["adventure"]) return of([]);

              const adventureData = data["adventure"];
              if (!adventureData.ids) return of({ games: [] });

              // ✅ Get userId from commonService instead of localStorage
              return this.commonService.getUid("AdventureComponent").pipe(
                switchMap((userId) => {
                  if (!userId) return of({ games: [] });

                  const observablesAdventure: Observable<any>[] = [];

                  adventureData.ids.map((id) => {
                    observablesAdventure.push(
                      combineLatest([
                        this.authService
                          .getAngularFireStore(
                            `/users/${userId}/game/adventure/${id}/details`,
                            "snapshotChanges"
                          )
                          .pipe(
                            map((res: Data) => {
                              try {
                                const userAdv = res?.payload?.data() as any;
                                return { userAdventure: userAdv };
                              } catch (e) {
                                return e;
                              }
                            })
                          ),
                        this.authService
                          .getAngularFireStore(
                            `/general_modules/game/${environment.mirrorPath}adventure/${id}`,
                            "snapshotChanges"
                          )
                          .pipe(
                            map((res: Data) => {
                              try {
                                const data = res?.payload?.data() as Data;
                                return data;
                              } catch (e) {
                                return e;
                              }
                            })
                          ),
                      ]).pipe(
                        map((advData) => {
                          if (
                            !advData ||
                            !advData[0] ||
                            !advData[0]?.userAdventure ||
                            !advData[0]?.userAdventure["prize_details"] ||
                            !advData[1]?.country_code_data?.[44]?.prize_details ||
                            !advData[0]?.userAdventure["prize_details"].prize_order
                          ) {
                            return { ...advData[0], ...advData[1] };
                          }

                          advData[0]["general_prize"] =
                            advData[1]?.country_code_data?.[44]?.prize_details[
                            advData[0]?.userAdventure["prize_details"].prize_order
                            ];
                          advData[0]["general_total_prize"] =
                            advData[1]?.country_code_data?.[44]?.total_prize;

                          return { ...advData[0], ...advData[1] };
                        })
                      )
                    );
                  });

                  return combineLatest(observablesAdventure).pipe(
                    map((advGame) => ({
                      games: advGame,
                      title: adventureData.title,
                      sub_title: adventureData.sub_title,
                    }))
                  );
                })
              );
            })
          )
        : of([])
    )
    );

    gameTopSlider$ = this.authService.getAngularFireStore(`/general_modules/${environment.mirrorPath}game`, "snapshotChanges").pipe(
    map((res: Data) => {
      try {
        const data = res?.payload?.data() as Data;
        if (data && data["game_slider"]) {
          return data["game_slider"];
        } else {
          return [];
        }
      } catch (e) {
        return [];
      }
    })
    );


    prizeUnlock$ = this.catchService.isOnline$
    .pipe(
      switchMap((isOnline) => {
        if (!isOnline) return of([]);

        // Modular Firestore API: Reference the collection
        const prizeUnlockCollectionRef = collection(this.firestore, `/general_modules/game/${environment.mirrorPath}prize_unlock`);

        // Use onSnapshot for real-time updates
        return new Observable((subscriber) => {
          const unsubscribe = onSnapshot(
            prizeUnlockCollectionRef,
            (snapshot) => {
              const actions = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              subscriber.next(actions);
            },
            (error) => {
              subscriber.error(error);
            }
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    )
    .pipe(
      map((res: any[]) => {
        res.map((prizeU) => {
          this.prizeUnlockID = prizeU?.id;
        });
        return this.prizeUnlockID;
      })
    );


    y2Play$ = this.userManagementService?.userRequest$.pipe(
        switchMap((user) =>
            user
            ? this.catchService.isOnline$.pipe(
                switchMap((isOnline) => {
                    if (!isOnline) return of([]);

                    return this.authService.getUid$("GamesRepositoryService").pipe(
                    switchMap((userId) => {
                        if (!userId) return of([]);

                        const yoursToPlayDocRef = doc(
                        this.firestore,
                        `/users/${userId}/game/yours_to_play`
                        );

                        const detailsCollectionRef = collection(
                        this.firestore,
                        `${yoursToPlayDocRef.path}/details`
                        );

                        return new Observable<any[]>((subscriber) => {
                        const unsubscribe = onSnapshot(
                            detailsCollectionRef,
                            (snapshot) => {
                            const actions = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            subscriber.next(actions);
                            },
                            (error) => subscriber.error(error)
                        );

                        return () => unsubscribe();
                        }).pipe(
                        this.catchService.retryWhen,
                        this.catchService.catchOfflineError
                        );
                    })
                    );
                }),
                this.catchService.retryWhen,
                this.catchService.catchOfflineError,


                switchMap((data) => {
                    if (!data || !Array.isArray(data) || data.length === 0) {
                    this.yoursToPlay$.next([]);
                    return of([]);
                    }

                    const gameStreams = data
                    .filter((game) => game && game.referance_doc)
                    .map(
                        (game) =>
                        new Observable((observer) => {
                            const unsubscribe = onSnapshot(
                            game.referance_doc,
                            (ref) => {
                                observer.next({
                                ...game,
                                details: ref?.data(),
                                });
                            },
                            (error) => observer.error(error)
                            );

                            return () => unsubscribe();
                        })
                    );

                    if (gameStreams.length === 0) {
                    this.yoursToPlay$.next([]);
                    return of([]);
                    }

                    return combineLatest(gameStreams).pipe(
                    map((games: any[]) => {
                        let gameYourPlay: any[] = [];

                        games.forEach((game) => {
                        if (!game) return;

                        if (
                            game?.play_count > 0 &&
                            game?.details?.game_type_id != 5
                        ) {
                            if (game?.catagory != "unlock") {
                            if (game?.details?.game_status == 1) {
                                gameYourPlay.push(game);
                            }
                            } else {
                            gameYourPlay.push(game);
                            }
                        }
                        });


                        const prizeIdx = gameYourPlay.findIndex(
                        (g) => g.game_id == this.prizeUnlockID
                        );

                        if (prizeIdx > 0) {
                        const [item] = gameYourPlay.splice(prizeIdx, 1);
                        gameYourPlay.unshift(item);
                        }

                        return gameYourPlay;
                    }),

                    tap((gameYourPlay) => {
                        this.yoursToPlay$.next(gameYourPlay);
                    }),

                    map(() => data)
                    );
                }),

                catchError((err) => {
                    return of([]);
                }),

                shareReplay(1)
                )
            : of([])
        )
    );

    prototypeGames = combineLatest([this.userManagementService.userRequest$, this.catchService.isOnline$, this.commonService.getUid('')]).pipe(
    switchMap(([user, isOnline, userId]) => {
      if (!user || !isOnline || !userId) return of([]);

      const detailsRef = collection(
        this.firestore,
        `users/${userId}/game/yours_to_play/details`
      );

      const prototypeQuery = query(
        detailsRef,
        where('category', '==', 'prototype')
      );
      return this.collectionSnapshot$(prototypeQuery);
    }),

    switchMap((games) => this.enrichGamesWithDetails$(games)),
    tap((enrichedGames) => this.yoursToPlay$.next(enrichedGames)),
    catchError(() => of([])),
    shareReplay(1)
  );


  private collectionSnapshot$(ref: CollectionReference | Query) {
      return new Observable((subscriber) => {
        const unsubscribe = onSnapshot(
          ref,
          (snapshot) => {
            const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            subscriber.next(docs);
          },
          (error) => subscriber.error(error)
        );
        return () => unsubscribe();
      })
    }


    private enrichGamesWithDetails$(games) {
    if (!games?.length) return of([]);

    const validGames = games.filter((g) => !!g?.referance_doc);
    if (!validGames.length) return of([]);

    const gameDetails$ = validGames.map((game) =>
      this.docSnapshot$(game.referance_doc).pipe(
        map((details) => ({ ...game, details })),
        startWith({ ...game, details: null })
      )
    );

    return combineLatest(gameDetails$).pipe(
      map((enrichedGames: Data) =>
        enrichedGames
          .filter((game) => {
            if (game?.play_count <= 0) return false;
            if (game?.details?.game_type_id === 5) return false;
            if (game?.catagory === 'unlock') return true;
            return game?.details?.game_status === 1;
          })
          .sort((a, b) => {
            if (a.game_id === this.prizeUnlockID) return -1;
            if (b.game_id === this.prizeUnlockID) return 1;
            return 0;
          })
      )
    );
  }

  private docSnapshot$<T>(ref: DocumentReference): Observable<T> {
    return new Observable<T>((subscriber) => {
      const unsubscribe = onSnapshot(
        ref,
        (snap) => subscriber.next(snap.data() as T),
        (error) => subscriber.error(error)
      );
      return () => unsubscribe();
    });
  }

}